/**
 * The one open day — the menu the trainee was given, and what they did to it.
 *
 * Separate from {@link useMonthLogV2} because it answers a different question off a different
 * endpoint: that one fetches a month of per-day totals with no meals at all, this one fetches a
 * single day with the whole plan and the whole log. The screen runs both, and their loading and
 * error states are kept apart on purpose — a month that fails to load must not blank the day a
 * trainer is reading, and a failed day must not blank the calendar.
 */

import { useEffect, useState } from "react";
import { getDayLog, type TraineeDayV2 } from "../../../../services/FoodLogService";

export const useDayLogV2 = (
  traineeId: string,
  /** YYYY-MM-DD */
  date: string
): { day: TraineeDayV2 | null; loading: boolean; error: unknown } => {
  const [day, setDay] = useState<TraineeDayV2 | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<unknown>(null);

  useEffect(() => {
    if (!traineeId || !date) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);

    getDayLog(traineeId, date)
      .then((result) => {
        if (!cancelled) setDay(result);
      })
      .catch((e) => {
        if (cancelled) return;
        setError(e);
        // Cleared rather than left behind. A failed fetch that kept the PREVIOUS day on screen would
        // show one day's food under another day's heading — and every number on it would look right.
        setDay(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [traineeId, date]);

  return { day, loading, error };
};
